import React from 'react';
import { Container, Typography, Grid, Card, CardContent, CardActions, Button, Box, Paper, Divider } from '@mui/material';
import { Build, Computer, Security, Speed, Support, Update, ArrowForward, CheckCircle } from '@mui/icons-material';

const Services = () => {
  const services = [
    {
      icon: <Build sx={{ fontSize: 40 }} />,
      title: 'Printer Repair',
      description: 'Professional repair services for all printer brands and models. We handle hardware issues, mechanical problems, and software conflicts.',
      price: 'From $49',
      features: ['Hardware diagnostics', 'Parts replacement', 'Software troubleshooting', 'Performance testing']
    },
    {
      icon: <Computer sx={{ fontSize: 40 }} />,
      title: 'Network Setup',
      description: 'Complete network printer setup and configuration. We ensure seamless integration with your existing network infrastructure.',
      price: 'From $79',
      features: ['Network configuration', 'Wireless setup', 'Security protocols', 'Multi-device connectivity']
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Security Solutions',
      description: 'Implement robust security measures to protect your printing infrastructure from unauthorized access and data breaches.',
      price: 'From $99',
      features: ['Access control', 'Encryption setup', 'Secure printing', 'Audit logging']
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'Performance Optimization',
      description: 'Enhance your printer\'s performance through regular maintenance and optimization techniques.',
      price: 'From $69',
      features: ['Speed calibration', 'Quality optimization', 'Resource management', 'Efficiency analysis']
    },
    {
      icon: <Support sx={{ fontSize: 40 }} />,
      title: '24/7 Support',
      description: 'Round-the-clock technical support for all your printer-related emergencies and queries.',
      price: 'From $199/month',
      features: ['Priority response', 'Remote assistance', 'Emergency service', 'Dedicated support team']
    },
    {
      icon: <Update sx={{ fontSize: 40 }} />,
      title: 'Software Updates',
      description: 'Regular software updates and driver installations to keep your printing system up-to-date and secure.',
      price: 'From $39',
      features: ['Driver updates', 'Firmware upgrades', 'Compatibility testing', 'Security patches']
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          position: 'relative',
          bgcolor: 'background.paper',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 16 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(180deg, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Services
            </Typography>
            <Typography 
              variant="h5" 
              component="h2" 
              color="text.secondary" 
              sx={{ mb: 4, lineHeight: 1.6 }}
            >
              Comprehensive Printer Support Solutions for Your Business
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ mb: 4, fontSize: '1.1rem' }}
            >
              We offer a wide range of professional printer support services to keep your business running smoothly. Our expert technicians are certified to work with all major printer brands and models.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} justifyContent="center">
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                    '& .service-icon': {
                      transform: 'scale(1.1)',
                      opacity: 0.9,
                    }
                  }
                }}
              >
                <Box 
                  className="service-icon"
                  sx={{ 
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '120px',
                    height: '120px',
                    background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                    opacity: 0.1,
                    borderRadius: '0 0 0 100%',
                    transition: 'all 0.3s ease-in-out',
                    zIndex: 0,
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: 4, position: 'relative', zIndex: 1 }}>
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3
                    }}
                  >
                    <Box 
                      sx={{ 
                        color: 'primary.main',
                        bgcolor: 'background.paper',
                        p: 2,
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h5" component="h3" fontWeight="600">
                      {service.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {service.description}
                  </Typography>
                  
                  <Box sx={{ mt: 3, mb: 2 }}>
                    {service.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle sx={{ color: 'primary.main', fontSize: 18, mr: 1 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  <Typography 
                    variant="h6" 
                    color="primary" 
                    sx={{ 
                      mt: 2,
                      fontWeight: 600
                    }}
                  >
                    {service.price}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 4, pt: 0 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    endIcon={<ArrowForward />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box 
          sx={{ 
            mt: 12, 
            textAlign: 'center',
            p: 6,
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
              zIndex: 0,
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Need a Custom Solution?
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              paragraph
              sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
            >
              Contact us for a personalized service package tailored to your specific needs.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              endIcon={<ArrowForward />}
              sx={{ 
                py: 1.5,
                px: 6,
                fontSize: '1.1rem',
                borderRadius: 2,
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Services; 